import React from 'react';
import { call, takeEvery } from 'redux-saga/effects';
import {
  SystemTenantMigrateForm,
  mountForm,
  unmountForm,
} from '@kineticdata/react';
import { generateFormButtons, FormError, Icon } from '@kineticdata/ui';
import { regRoute, regSaga } from '../../store';
import { navigate } from '../../root';
import { Link, useParams } from 'react-router-dom';
import {
  DATABASE_ADAPTER_FIELDS,
  MssqlPropertyLayout,
  OraclePropertyLayout,
  PostgresPropertyLayout,
} from '../common/DatabaseAdapters';

const formKey = 'tenant-migrate';

const handleSave =
  () =>
  ({ tenant }) => {
    if (tenant) {
      // If we got a `tenant` then this was a create and we redirect to the edit.
      navigate(`/spaces/edit/${tenant.slug}`);
    }
  };

regRoute('TENANT_MIGRATE_PAGE', '/spaces/migrate/:spaceSlug/*');

regSaga(
  takeEvery('ENTER_TENANT_MIGRATE_PAGE', function* () {
    yield call(mountForm, formKey);
  }),
);

regSaga(
  takeEvery('LEAVE_TENANT_MIGRATE_PAGE', function* () {
    yield call(unmountForm, formKey);
  }),
);

const FormLayout = ({ fields, error, buttons, bindings }) => {
  const currentTaskAdapter = bindings.values.get('task_databaseAdapter_type');
  const currentIntegratorAdapter = bindings.values.get(
    'integrator_databaseAdapter_type',
  );

  return (
    <form>
      <div className="content-container p-4 pb-0 mb-4">
        <div className="row">
          <div className="col-5">{fields.get('name')}</div>
          <div className="col-5">{fields.get('slug')}</div>
          <div className="col-2">{fields.get('coreServiceName')}</div>
        </div>
        <div className="row">
          <div className="col-6">{fields.get('integrationUsername')}</div>
          <div className="col-6">{fields.get('integrationPassword')}</div>
        </div>
        <div className="row">
          <div className="col-6">{fields.get('configuratorUsername')}</div>
          <div className="col-6">{fields.get('configuratorPassword')}</div>
        </div>
      </div>

      <div className="content-container p-4 pb-0 mb-4">
        <div className="row">
          <div className="col-12">{fields.get('encryptionKey')}</div>
        </div>
        <div className="row">
          <div className="col-12">{fields.get('vaultKey')}</div>
        </div>
        <div className="row">
          <div className="col-12">{fields.get('secretKeyBase')}</div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-s1536-6">
          <div className="content-container p-4 pb-0 mb-4">
            <div className="d-flex justify-content-between">
              <h3 className="mb-4">
                <Icon name="workflow" bg />
                <span className="ms-2">Task</span>
              </h3>
              {fields.get('task_feature')}
            </div>
            {bindings.values.get('task_feature') && (
              <>
                <div className="row">
                  <div className="col-5">{fields.get('task_replicas')}</div>
                  <div className="col-7">{fields.get('task_imageToggle')}</div>
                  <div className="col-12">{fields.get('task_image')}</div>
                  <div className="col-5">
                    {fields.get('task_databaseAdapter_type')}
                  </div>
                  <div className="col-7">
                    {fields.get('task_autoCreateDatabase')}
                  </div>
                </div>
                {currentTaskAdapter === 'mssql' ? (
                  <MssqlPropertyLayout
                    fields={fields}
                    values={bindings.values}
                    buildFieldName={name => `task_mssql_${name}`}
                  />
                ) : currentTaskAdapter === 'oracle' ? (
                  <OraclePropertyLayout
                    fields={fields}
                    values={bindings.values}
                    buildFieldName={name => `task_oracle_${name}`}
                  />
                ) : currentTaskAdapter === 'postgres' &&
                  !bindings.values.get('task_autoCreateDatabase') ? (
                  <PostgresPropertyLayout
                    fields={fields}
                    values={bindings.values}
                    buildFieldName={name => `task_postgres_${name}`}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className="col-12 col-s1536-6">
          <div className="content-container p-4 pb-0 mb-4">
            <div className="d-flex justify-content-between">
              <h3 className="mb-4">
                <Icon name="plug" bg />
                <span className="ms-2">Integrator</span>
              </h3>
              {fields.get('integrator_feature')}
            </div>
            {bindings.values.get('integrator_feature') && (
              <>
                <div className="row">
                  <div className="col-5">
                    {fields.get('integrator_replicas')}
                  </div>
                  <div className="col-7">
                    {fields.get('integrator_imageToggle')}
                  </div>
                  <div className="col-12">{fields.get('integrator_image')}</div>
                  <div className="col-5">
                    {fields.get('integrator_databaseAdapter_type')}
                  </div>
                  <div className="col-7">
                    {fields.get('integrator_autoCreateDatabase')}
                  </div>
                </div>
                {currentIntegratorAdapter === 'postgres' &&
                !bindings.values.get('integrator_autoCreateDatabase') ? (
                  <PostgresPropertyLayout
                    fields={fields}
                    values={bindings.values}
                    buildFieldName={name => `integrator_postgres_${name}`}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {error}
      <div className="form-buttons">{buttons}</div>
    </form>
  );
};

export const SpaceMigrate = props => {
  const { spaceSlug } = useParams();

  return (
    <div className="app-content__main">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/spaces">Spaces</Link>
          </li>
          <li className="breadcrumb-item active">Migrate Space</li>
        </ol>
      </nav>
      <div className="heading">
        <h1>Migrate Space</h1>
      </div>
      <SystemTenantMigrateForm
        formKey={formKey}
        slug={spaceSlug}
        onSave={handleSave}
        components={{
          FormError,
          FormButtons: generateFormButtons({
            submitLabel: 'Migrate Space',
            cancelPath: '/spaces',
          }),
          FormLayout,
        }}
        alterFields={{
          task_feature: {
            label: '',
            renderAttributes: { switch: true },
          },
          task_autoCreateDatabase: {
            renderAttributes: { wrapperClassName: 'pt-v32' },
          },
          task_replicas: {
            constraint: ({ values }) =>
              !values.get('task_feature') ||
              (typeof values.get('task_replicas') === 'number' &&
                values.get('task_replicas') > 0),
            constraintMessage: 'Replica count must be a positive integer.',
            renderAttributes: { min: 1 },
          },
          integrator_feature: {
            label: '',
            renderAttributes: { switch: true },
          },
          integrator_autoCreateDatabase: {
            renderAttributes: { wrapperClassName: 'pt-v32' },
          },
          integrator_replicas: {
            constraint: ({ values }) =>
              !values.get('integrator_feature') ||
              (typeof values.get('integrator_replicas') === 'number' &&
                values.get('integrator_replicas') > 0),
            constraintMessage: 'Replica count must be a positive integer.',
            renderAttributes: { min: 1 },
          },
          task_image: { renderAttributes: { inlineHelpText: true } },
          task_imageToggle: {
            renderAttributes: { wrapperClassName: 'pt-v32' },
          },
          integrator_image: { renderAttributes: { inlineHelpText: true } },
          integrator_imageToggle: {
            renderAttributes: { wrapperClassName: 'pt-v32' },
          },
          ...DATABASE_ADAPTER_FIELDS(),
        }}
      />
    </div>
  );
};
